import { type DeviceEntity, type DeviceId } from '@solinkcorp/api-types';
import { getAccessibleAddress } from '@solinkcorp/utils';
import React, { createContext, useContext, useMemo } from 'react';

export interface DeviceLocalityProvider {
  useDeviceIsLocal(deviceId: DeviceId | undefined): boolean | undefined;
}

const DeviceLocalityContext = createContext<DeviceLocalityProvider | null>(null);

export function useDeviceIsLocal(deviceId: DeviceId | undefined): boolean | undefined {
  const provider = useContext(DeviceLocalityContext);
  if (!provider) {
    throw new Error('useDeviceIsLocal must be used within a DeviceLocalityProviderWrapper');
  }
  return provider.useDeviceIsLocal(deviceId);
}

export interface AuthLocation {
  id: string;
  name: string;
  accessibleAddress: string;
  currentSoftwareVersion: string;
}

export function useAuthDevice(device: DeviceEntity | undefined): AuthLocation | undefined {
  const isLocal = useDeviceIsLocal(device?.id);
  const accessibleAddress = device
    ? getAccessibleAddress(
        {
          deviceUrl: device.deviceUrl,
          deviceLocalUrl: device.deviceLocalUrl,
          deviceUrlV2: device.deviceUrlV2,
        },
        isLocal,
      )
    : '';
  const deviceId = device?.id;
  const deviceName = device?.name ?? '';
  const currentSoftwareVersion = device?.currentSoftwareVersion ?? '';

  return useMemo(
    () =>
      deviceId
        ? {
            id: deviceId,
            name: deviceName,
            accessibleAddress: accessibleAddress ?? '',
            currentSoftwareVersion,
          }
        : undefined,
    [deviceId, deviceName, accessibleAddress, currentSoftwareVersion],
  );
}

export function DeviceLocalityProviderWrapper({
  provider,
  children,
}: {
  provider: DeviceLocalityProvider;
  children: React.ReactNode;
}) {
  return (
    <DeviceLocalityContext.Provider value={provider}>{children}</DeviceLocalityContext.Provider>
  );
}
